import {apiFetch, handleResponse, JSON_HEADERS} from "./ApiService";

const BASE_URL = '/api/v1/merchants';

export type MerchantResponse = {
    merchantId: string;
    reportApiKey: boolean;
    clientKey: boolean;
    adyenPrefix: string;
}

type Merchant = {
    merchantId?: string;
    apiKey?: string;
    reportApiKey?: string;
    clientKey?: string;
    adyenPrefix?: string;
};

type MerchantConfiguration = {
    merchantId: string;
    configurationName: string;
    configurationValue: string;
}

export const ERROR_MESSAGES = {
    fetchFail: 'Failed to fetch merchants',
    createFail: 'Failed to create merchant account',
    updateFail: 'Failed to update merchant',
    deleteFail: 'Failed to delete merchant',
    fetchConfigurationFail: 'Failed to fetch merchant configuration',
    setConfigurationFail: 'Failed to set merchant configuration',
};

export const fetchMerchants = async (): Promise<MerchantResponse[]> => {
    const response = await apiFetch(BASE_URL, {method: 'GET', credentials: 'include'});
    handleResponse(response, ERROR_MESSAGES.fetchFail);
    return await response.json();
};

export const createMerchant = async (merchant: Merchant): Promise<void> => {
    const response = await apiFetch(BASE_URL, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(merchant)
    });
    if (response.status === 409) {
        throw new Error('Merchant account already exists.');
    }
    if (response.status === 400) {
        throw new Error('Could not create merchant account. Please check your input and try again.');
    }
    handleResponse(response, ERROR_MESSAGES.createFail);
};


export const updateMerchant = async (merchantId: string, updatedData: Partial<Merchant>): Promise<void> => {
    const response = await apiFetch(`${BASE_URL}/${merchantId}`, {
        method: 'PUT',
        headers: JSON_HEADERS,
        body: JSON.stringify(updatedData)
    });
    if (response.status === 404) {
        throw new Error('Merchant account not found.');
    }
    handleResponse(response, ERROR_MESSAGES.updateFail);
};

export const deleteMerchant = async (merchantId: string): Promise<void> => {
    const response = await apiFetch(`${BASE_URL}/${merchantId}`, {method: 'DELETE'});
    if (response.status === 404) {
        throw new Error('Merchant account not found.');
    }
};

export const getMerchantConfiguration = async (
    merchantId: string,
    configurationName: string
): Promise<string> => {
    const response = await apiFetch(`${BASE_URL}/${merchantId}/configurations/${configurationName}`, {method: 'GET'});
    handleResponse(response, ERROR_MESSAGES.fetchConfigurationFail);
    return await response.text();
};

export const setMerchantConfiguration = async (config: MerchantConfiguration): Promise<void> => {
    const response = await apiFetch(`${BASE_URL}/${config.merchantId}/configurations`, {
        method: 'PUT',
        headers: JSON_HEADERS,
        body: JSON.stringify(config),
    });
    handleResponse(response, ERROR_MESSAGES.setConfigurationFail);
};
